/** @jsx isolateComponent */

import { camelCase } from 'lodash';
import { Fragment, useContext, useEffect, useState } from 'react';

import { css } from '@emotion/core';
import { Forms, Spacing } from '@zapier/theme';
import { isolateComponent } from '@zapier/common-utils';

import CodeEditor from 'app/developer-v3/components/CodeEditor';
import FieldMessage, {
  FieldType,
} from 'app/developer-v3/components/FieldMessage';
import { SuggestionsContext } from 'app/common/components/AutoCompleteTextInput/SuggestionsContext';
import {
  convertKvpsToObj,
  getBorderColor,
  requestCode,
  urlCode,
} from 'app/developer-v3/utils';

import AdvancedSettings from './AdvancedSettings';
import BasicUrl from './BasicUrl';
import DropdownStateful from './DropdownStateful';
import ToggleButton from './ToggleButton';
import ToggleModal from './ToggleModal';
import { DEFAULT_INITIAL_TABS } from './constants';
import { stripDangerousURIs } from './utils';

import { RequestMethodTypes } from 'app/developer-v3/types/request';
import type { ErrorList } from 'app/developer-v3/types/validator';
import type { KeyValue } from 'app/developer-v3/components/KeyValueEditor';
import type { ViolationTypes } from 'app/developer-v3/types';
import type {
  RequestOptions,
  RequestSchema,
} from 'app/developer-v3/platformSchema/request';

const Styles = {
  requestEditor: (borderColor?: string) => [
    css`
      border: ${Forms.field.border};
      border-radius: ${Forms.field.borderRadius};
    `,
    borderColor &&
      css`
        border-color: ${borderColor};
      `,
  ],
  code: css`
    padding: ${Spacing.medium}px;
  `,
  toggleWrapper: css`
    display: block;
  `,
};

type Props = {
  codeIsUrlOnly?: boolean;
  codeReturnKeys?: Array<string>;
  codeReturnMessage?: string;
  defaultCode: string;
  disabledTabs: Array<'body' | 'options'>;
  errors: ErrorList | string;
  info?: {
    type: ViolationTypes;
    message: string;
  };
  labelText: string;
  name: string;
  onChange: (valueNext: {}) => void;
  value: RequestSchema;
};

const defaultProps = {
  defaultCode: '',
  disabledTabs: [],
  errors: {},
};

function RequestEditor({
  codeIsUrlOnly,
  codeReturnKeys,
  codeReturnMessage,
  defaultCode = defaultProps.defaultCode,
  disabledTabs = defaultProps.disabledTabs,
  errors = defaultProps.errors,
  info,
  labelText,
  name,
  onChange,
  value,
}: Props) {
  const [isCode, setIsCode] = useState(!!value?.source);

  const { loadSuggestions } = useContext(SuggestionsContext);

  const [url, setUrl] = useState(isCode ? '' : value?.url ?? '');

  const [method, setMethod] = useState<RequestMethodTypes>(
    isCode ? RequestMethodTypes.GET : value?.method ?? RequestMethodTypes.GET
  );

  const [params, setParams] = useState(isCode ? {} : value?.params ?? {});
  const [headers, setHeaders] = useState<{}>(
    isCode ? {} : value?.headers ?? {}
  );
  const [body, setBody] = useState(isCode ? {} : value?.body ?? {});
  const [removeMissingValuesFrom, setRemoveMissingValuesFrom] = useState<
    RequestOptions
  >(
    isCode
      ? {}
      : value?.removeMissingValuesFrom ?? {
          body: false,
          params: false,
        }
  );
  const [source, setSource] = useState(
    isCode ? value?.source ?? defaultCode : defaultCode
  );

  useEffect(() => {
    loadSuggestions(name);
  }, [loadSuggestions, name]);

  const updateUrl = (urlNext: string) => {
    setUrl(() => {
      onChange({ ...value, url: urlNext });
      return urlNext;
    });
  };

  const normalizeUrl = () => {
    const urlNext =
      !url || url.includes('://') || url.startsWith('{')
        ? url
        : `https://${url}`;
    updateUrl(stripDangerousURIs(urlNext));
  };

  const updateMethod = (methodNext: RequestMethodTypes) => {
    setMethod(() => {
      onChange({ ...value, method: methodNext });
      return methodNext;
    });
  };

  const updateParams = (items: Array<KeyValue>) => {
    const paramsNext = convertKvpsToObj(items);
    setParams(() => {
      onChange({ ...value, params: paramsNext });
      return paramsNext;
    });
  };

  const updateHeaders = (items: Array<KeyValue>) => {
    setHeaders(() => {
      const headersNext = convertKvpsToObj(items);
      onChange({ ...value, headers: headersNext });
      return headersNext;
    });
  };

  const updateBody = (items: Array<KeyValue>) => {
    setBody(() => {
      const bodyNext = convertKvpsToObj(items);
      onChange({ ...value, body: bodyNext });
      return bodyNext;
    });
  };

  const updateRemoveMissingValuesFrom = (
    optionKey: string,
    optionValue: boolean
  ) => {
    const removeMissingValuesFromNext = {
      ...removeMissingValuesFrom,
      [optionKey]: optionValue,
    };
    setRemoveMissingValuesFrom(() => {
      onChange({
        ...value,
        removeMissingValuesFrom: removeMissingValuesFromNext,
      });
      return removeMissingValuesFromNext;
    });
  };

  const updateSource = (sourceNext: string) => {
    setSource(() => {
      onChange({ ...value, source: sourceNext });
      return sourceNext;
    });
  };

  const switchMode = () => {
    const isCodeNext = !isCode;

    if (isCodeNext) {
      if (source) {
        onChange({ source });
      } else {
        const sourceNext = codeIsUrlOnly
          ? urlCode(url, params)
          : requestCode(
              { url, method, params, headers, body, removeMissingValuesFrom },
              codeReturnKeys,
              codeReturnMessage
            );
        setSource(sourceNext);
        onChange({ source: sourceNext });
      }
    } else {
      onChange({
        body,
        headers,
        method,
        params,
        removeMissingValuesFrom,
        url,
      });
    }

    setIsCode(isCodeNext);
  };

  return (
    <Fragment>
      <div
        aria-label={`Request editor - ${labelText}`}
        css={Styles.requestEditor(info && getBorderColor(info.type))}
      >
        {isCode ? (
          <CodeEditor
            css={Styles.code}
            mode="javascript"
            onChange={updateSource}
            value={source}
          />
        ) : (
          <Fragment>
            <BasicUrl
              error={errors[name]}
              labelText={labelText}
              method={method}
              onBlurUrl={normalizeUrl}
              onChangeMethod={updateMethod}
              onChangeUrl={updateUrl}
              url={url}
            />
            <AdvancedSettings
              body={body}
              disabledTabs={[
                ...disabledTabs,
                ...(['GET', 'HEAD'].includes(method) ? ['body' as 'body'] : []),
              ]}
              headers={headers}
              initialActiveTab={DEFAULT_INITIAL_TABS[method]}
              onChangeBody={updateBody}
              onChangeHeaders={updateHeaders}
              onChangeOptions={updateRemoveMissingValuesFrom}
              onChangeParams={updateParams}
              params={params}
              removeMissingValuesFrom={removeMissingValuesFrom}
            />
          </Fragment>
        )}
        <DropdownStateful
          css={Styles.toggleWrapper}
          items={[]}
          renderMenu={props => (
            <ToggleModal
              isCode={isCode}
              onClickButton={switchMode}
              {...props}
            />
          )}
          renderToggle={props => (
            <ToggleButton isCode={isCode} labelText={labelText} {...props} />
          )}
        />
      </div>
      {info && (
        <div>
          <FieldMessage
            fieldKey="sample-output-editor"
            message={info.message}
            type={camelCase(`info ${info.type}`) as FieldType}
          />
        </div>
      )}
    </Fragment>
  );
}

RequestEditor.defaultProps = defaultProps;

export default RequestEditor;
